<template>
  <div class="">
    <div class="flex justify-between flex-wrap items-center py-2">
      <div class="w-1/5 min-w-[200px] pr-5 text-xs py-2">
        Search (Country name)
        <v-text-field placeholder="Name" variant="outlined" density="compact" v-model="searchData" rounded="0">
        </v-text-field>
      </div>
      <div>
        <v-btn icon="mdi-delete" size="small" style="outline: none;" @click="deleteSelected"
          class="mr-5 bg-dark text-light"></v-btn>
        <v-btn icon="mdi-plus" size="small" style="outline: none;" class="bg-dark text-light" data-bs-toggle="modal"
          data-bs-target="#countryAddModal"></v-btn>
      </div>
    </div>

    <v-data-table :headers="headers" :items="filteredCountries" item-key="id"
      class="border h-[500px] bg-white text-black min-h-[500px]" items-per-page="-1" style="font-size: 13px;"
      :style="`height: ${computedHeight}px;`">
      <template v-slot:[`item.checkbox`]="{ item }">
        <div @click="toggleSelection(item)" class="ml-auto align-self-center" style="width: 20px; height: 20px; cursor: pointer;">
          <input :checked="isSelected(item)" class="form-check-input shadow-none" type="checkbox" />
        </div>
      </template>
      <template v-slot:[`item.iconurl`]="{ item }">
        <div class="w-full h-full flex items-center justify-center">
          <v-img v-if="item.iconurl" :src="item.iconurl" class="flag-img" contain></v-img>
          <span v-else class="text-gray-400 text-xs">-</span>
        </div>
      </template>
      <template v-slot:[`item.sellable`]="{ item }">
        <div class="w-full h-full flex justify-center items-center">
          <v-switch v-model="item.sellable" color="success" @click="toggleSellable(item)"></v-switch>
        </div>
      </template>
      <template v-slot:[`item.buttonBar`]="{ item }">
        <v-menu offset-y location="bottom">
          <template v-slot:activator="{ props, on, isActive }">
            <v-btn v-bind="props" v-on="{ ...on }" density="compact"
              :style="isActive ? 'border-bottom-left-radius: 0px;border-bottom-right-radius: 0px;' : ''"
              style="outline: none; font-size: 10px;" class="w-32 z-10 bg-dark text-light">
              Actions
              <v-icon icon="mdi-chevron-down" size="14"></v-icon>
            </v-btn>
          </template>
          <v-list class="w-32 flex-1 flex flex-col px-0 py-0 z-0"
            style="border-top-left-radius: 0px;border-top-right-radius: 0px">
            <v-btn v-for="(button, index) in actionButtons" :key="index" @click="clickAction(item, button)" rounded="0"
              class="text-center bg-dark text-light"
              style="font-size: 10px; width: 128px; outline: none; border-bottom: 1px solid #345;"
              :data-bs-toggle="button === 'Update' && 'modal'" :data-bs-target="button === 'Update' && '#countryUpdateModal'">
              {{ button }}
            </v-btn>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>
  </div>

  <AddNewOneDialog @created="reload" />
  <UpdateInfoDialog :item="selectedItem" @updated="reload" />
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { storeToRefs } from "pinia";
import { useAdminCountryStore } from "@/stores";

import AddNewOneDialog from "./Modal/AddNewOneDialog.vue";
import UpdateInfoDialog from "./Modal/UpdateInfoDialog.vue";

const adminCountryStore = useAdminCountryStore();
const { countries } = storeToRefs(adminCountryStore);

adminCountryStore.loadCountries();

const headers = ref([
  { title: "", align: "center", key: "checkbox" },
  { title: "ID", align: "center", key: "id" },
  { title: "Country Name", align: "center", key: "name" },
  { title: "Flag", align: "center", key: "iconurl" },
  { title: "Sellable", align: "center", key: "sellable" },
  { title: "Order", align: "center", key: "order" },
  { title: "Actions", align: "center", key: "buttonBar" },
]);

const searchData = ref("");
const selectedItems = ref([]);
const selectedItem = ref(null);

const actionButtons = ref(["Update", "Delete"]);

const offsetHeight = 300;
const computedHeight = ref(window.innerHeight - offsetHeight);
const handleResize = () => {
  computedHeight.value = window.innerHeight - offsetHeight;
};
onMounted(() => window.addEventListener("resize", handleResize));
onBeforeUnmount(() => window.removeEventListener("resize", handleResize));

const filteredCountries = computed(() => {
  const q = searchData.value.toLowerCase();
  return (countries.value || []).filter((c) => String(c.name || "").toLowerCase().includes(q));
});

const isSelected = (item) => selectedItems.value.includes(item);
const toggleSelection = (item) => {
  const idx = selectedItems.value.indexOf(item);
  if (idx === -1) selectedItems.value.push(item);
  else selectedItems.value.splice(idx, 1);
};

const reload = async () => {
  selectedItems.value = [];
  selectedItem.value = null;
  await adminCountryStore.loadCountries();
};

const deleteSelected = async () => {
  if (!selectedItems.value.length) return;
  await adminCountryStore.deleteCountries(selectedItems.value.map((i) => i.id));
  await reload();
};

const toggleSellable = async (item) => {
  await adminCountryStore.updateCountry({ id: item.id, sellable: !item.sellable });
  await adminCountryStore.loadCountries();
};

const clickAction = async (item, button) => {
  switch (button) {
    case "Update":
      selectedItem.value = item;
      break;
    case "Delete":
      await adminCountryStore.deleteCountries([item.id]);
      await reload();
      break;
  }
};
</script>

<style scoped>
.flag-img {
  width: 70px;
  height: 44px;
}

/* Increase row height so flags show fully */
:deep(.v-data-table .v-data-table__tr) {
  height: 64px;
}
:deep(.v-data-table .v-data-table__td) {
  padding-top: 8px;
  padding-bottom: 8px;
}
</style>

